import { useState } from "react";
import type { Product } from "./catalog";
import { CartButton } from "./CartButton";
import { CartDrawer } from "./CartDrawer";
import { CustomerPicker } from "./CustomerPicker";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { OrderConfirmation, type OrderSummary } from "./OrderConfirmation";
import { ProductGrid } from "./ProductGrid";
import { type Me, signOut } from "./session";

export type CatalogState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; products: Product[] };

export function App({ catalog, me }: { catalog: CatalogState; me: Me }) {
  const [cartOpen, setCartOpen] = useState(false);
  // Set on a successful checkout (#187): the confirmation view takes over
  // the page until "Continue shopping".
  const [placedOrder, setPlacedOrder] = useState<OrderSummary | null>(null);

  return (
    <div className="page">
      <ImpersonationBanner me={me} />
      <header className="site-header">
        <div className="site-header-text">
          <h1>Storefront</h1>
          <p className="tagline">A small shop, five languages under the hood.</p>
        </div>
        <div className="site-header-actions">
          {!placedOrder && (
            <>
              <CustomerPicker />
              <CartButton onClick={() => setCartOpen(true)} />
            </>
          )}
          <span className="signed-in">
            {me.name || me.email || me.sub}
            <button type="button" className="signout-link" onClick={() => signOut("/")}>
              Sign out
            </button>
          </span>
        </div>
      </header>

      <main>
        {placedOrder ? (
          <OrderConfirmation summary={placedOrder} onDone={() => setPlacedOrder(null)} />
        ) : (
          <>
            {catalog.status === "loading" && (
              <p className="status-message">Loading products…</p>
            )}
            {catalog.status === "error" && (
              <p className="status-message status-error">
                Couldn't load the catalog ({catalog.message}).
              </p>
            )}
            {catalog.status === "ready" && <ProductGrid products={catalog.products} />}
          </>
        )}
      </main>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onPlaced={(summary) => setPlacedOrder(summary)}
      />
    </div>
  );
}
